import { useCallback } from "react";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { QueryClient, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { t } from "i18next";
import { auth } from "../firebase";
import { useAppDispatch } from "../state/hooks";
import { setLoader } from "../state/loader/reducer";
import { clearListCache } from "../utils/listStateCache";
import { clearVisitedOffers } from "../utils/visitedOffers";

/**
 * Drops what this tab keeps for the signed-in user. Every user-scoped query is cached under a
 * fixed key ("Wallet", "Conversations", ...), and the restorable lists and visited offers sit in
 * sessionStorage, so without this the next person to sign in on this tab is served the previous
 * one's balance, threads, favourites and history.
 */
export function forgetUserData(queryClient: QueryClient) {
  queryClient.clear();
  clearListCache();
  clearVisitedOffers();
}

/** Sign out, tell the user, and land on the home page. Shared by the header, the mobile bar and the settings. */
export default function useLogout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useCallback(() => {
    dispatch(setLoader(true));
    signOut(auth)
      .then(() => {
        forgetUserData(queryClient);
        enqueueSnackbar(t("loggedOut", { ns: "action", defaultValue: "Wylogowano" }), { variant: "success" });
        navigate("/");
      })
      .catch((error) => {
        console.error("Error signing out: ", error);
        enqueueSnackbar(t("logOutError", { ns: "action", defaultValue: "Nie udało się wylogować. Spróbuj ponownie." }), { variant: "error" });
      })
      .finally(() => {
        dispatch(setLoader(false));
      });
  }, [dispatch, enqueueSnackbar, navigate, queryClient]);
}
